import { useEffect, useMemo } from "react";
import { Droplet, TimerReset, X } from "lucide-react";
import {
  formatPastReadingDisplayValue,
  type FingerPrickPastReading,
} from "../utils/measurePageMappers";
import {
  measureCardMeta,
  measureCardValue,
  measureEyebrow,
  measureSectionTitle,
} from "../utils/measurePageTypography";
import { cn } from "../lib/utils";

export type FingerPrickPastListSheetProps = {
  open: boolean;
  onClose: () => void;
  readings: FingerPrickPastReading[];
  loading?: boolean;
  dateLabel?: string;
};

const isFingerMealReading = (reading: FingerPrickPastReading): boolean =>
  reading.typeLabel === "Finger prick" || Boolean(reading.mealTimingLabel);

const renderReadingRow = (reading: FingerPrickPastReading) => (
  <div className="flex items-center gap-3 rounded-2xl border border-border/80 bg-card p-4 shadow-sm">
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-sky-200/70 bg-sky-50/90 dark:border-sky-900/45 dark:bg-sky-950/35">
      <Droplet className="h-5 w-5 text-primary" strokeWidth={1.85} />
    </span>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="inline-flex rounded-md bg-primary/10 px-2 py-0.5 text-2xs font-bold uppercase tracking-[0.08em] text-primary">
          {reading.typeLabel}
        </span>
        {reading.mealTimingLabel ? (
          <span
            className={cn(
              "inline-flex rounded-md px-2 py-0.5 text-2xs font-bold uppercase tracking-[0.08em]",
              reading.mealTimingLabel === "Pre-meal"
                ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                : "bg-amber-500/10 text-amber-700 dark:text-amber-400",
            )}
          >
            {reading.mealTimingLabel}
          </span>
        ) : null}
      </div>
      <p className="mt-1.5 text-2xs font-bold uppercase tracking-[0.06em] text-muted-foreground">
        {reading.meta}
      </p>
      <p className={measureCardValue}>{formatPastReadingDisplayValue(reading)}</p>
    </div>
  </div>
);

const FingerPrickPastListSheet = ({
  open,
  onClose,
  readings,
  loading = false,
  dateLabel,
}: FingerPrickPastListSheetProps) => {
  const { preRows, postRows, otherRows } = useMemo(() => {
    const preRows = readings.filter(
      (r) => r.mealTimingLabel === "Pre-meal" && isFingerMealReading(r),
    );
    const postRows = readings.filter(
      (r) => r.mealTimingLabel === "Post-meal" && isFingerMealReading(r),
    );

    // Collect all values already shown in pre/post sections (normalize whitespace)
    const shownValues = new Set(
      [...preRows, ...postRows].map((r) => r.value.replace(/\s/g, "").toLowerCase()),
    );

    const otherRows = readings.filter((r) => {
      // Already in pre/post sections — skip
      if (r.mealTimingLabel && isFingerMealReading(r)) return false;
      // Same value already shown above — skip (dedup across sources)
      if (shownValues.has(r.value.replace(/\s/g, "").toLowerCase())) return false;
      return true;
    });

    return {
      preRows,
      postRows,
      otherRows,
    };
  }, [readings]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col justify-end"
      role="dialog"
      aria-modal="true"
      aria-labelledby="finger-past-sheet-title"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />

      <div className="relative z-10 mx-auto w-full max-w-[400px] duration-200 animate-in slide-in-from-bottom-4">
        <div className="flex max-h-[min(92dvh,_820px)] flex-col overflow-hidden rounded-t-3xl border border-border bg-background shadow-2xl ring-1 ring-border/80">
          <div className="flex justify-center pb-2 pt-3">
            <div className="h-1 w-11 rounded-full bg-muted-foreground/25" aria-hidden />
          </div>

          <div className="shrink-0 border-b border-border/70 px-5 pb-4 pt-0.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className={measureEyebrow}>Glucose readings · History</p>
                <h2 id="finger-past-sheet-title" className={`mt-1 ${measureSectionTitle}`}>
                  Reading history
                </h2>
                {!loading && readings.length > 0 ? (
                  <p className={`mt-1 ${measureCardMeta}`}>
                    {readings.length} logged{dateLabel ? ` · ${dateLabel}` : ""}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-muted"
                aria-label="Close"
              >
                <X className="h-5 w-5" strokeWidth={2} />
              </button>
            </div>
          </div>

          <div className="scrollbar-hide min-h-0 flex-1 overflow-y-auto px-5 py-4">
            {loading ? (
              <div className="space-y-3">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-[72px] animate-pulse rounded-2xl border border-border bg-muted/40"
                    aria-hidden
                  />
                ))}
              </div>
            ) : readings.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-muted/20 px-4 py-10 text-center">
                <TimerReset className="mx-auto h-8 w-8 text-muted-foreground/50" strokeWidth={1.5} />
                <p className="mt-3 text-sm font-medium text-foreground">No readings yet</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Use Log now on the card to add a glucose reading.
                </p>
              </div>
            ) : (
              <div className="space-y-4">

                {(preRows.length > 0 || postRows.length > 0) && (
                  <div className="space-y-4">
                    {preRows.length > 0 ? (
                      <section aria-label="Pre-meal readings">
                        <p className={`mb-2 ${measureEyebrow}`}>Pre-meal log</p>
                        <ul className="space-y-3">
                          {preRows.map((reading, index) => (
                            <li key={`pre-${reading.value}-${reading.meta}-${index}`}>
                              {renderReadingRow(reading)}
                            </li>
                          ))}
                        </ul>
                      </section>
                    ) : null}
                    {postRows.length > 0 ? (
                      <section aria-label="Post-meal readings">
                        <p className={`mb-2 ${measureEyebrow}`}>Post-meal log</p>
                        <ul className="space-y-3">
                          {postRows.map((reading, index) => (
                            <li key={`post-${reading.value}-${reading.meta}-${index}`}>
                              {renderReadingRow(reading)}
                            </li>
                          ))}
                        </ul>
                      </section>
                    ) : null}
                  </div>
                )}

                {otherRows.length > 0 ? (
                  <section aria-label="Other glucose readings">
                    {preRows.length > 0 || postRows.length > 0 ? (
                      <p className={`mb-2 ${measureEyebrow}`}>Other readings</p>
                    ) : null}
                    <ul className="space-y-3">
                      {otherRows.map((reading, index) => (
                        <li
                          key={`${reading.typeLabel}-${reading.mealTimingLabel ?? "none"}-${reading.value}-${reading.meta}-${index}`}
                        >
                          {renderReadingRow(reading)}
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FingerPrickPastListSheet;
